<template>
  <section>
    <notifications></notifications>

    <base-header>
      <div class="row align-items-center pt-header">
        <div
          class="col-lg-4 col-md-6 col-sm-6 col-12 d-flex align-items-center"
        >
          <el-tooltip content="Back" placement="bottom">
            <base-button
              type="neutral"
              class="btn-icon-only"
              @click="goToPreviousPage()"
            >
              <i class="flaticon-left-1"></i>
            </base-button>
          </el-tooltip>
          <h1 class="title-view">{{ $route.meta.title }}</h1>
        </div>
        <div class="col-lg-8 col-md-6 col-sm-6 col-12 alinmov3">
          <div class="box-buttons"></div>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mb-5">
      <div class="row">
        <div
          v-if="hasUserAccess(userRole, ROLE_PERMISSIONS.VEHICLE_GALLERY)"
          class="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-12 box-flex"
        >
          <div
            class="card card-shadow p-3 card-folder"
            @click="$router.push('/tools/vehicles-gallery')"
          >
            <div class="box-folder">
              <div class="icon-folder primary">
                <i class="flaticon-tools-image-gallery"></i>
              </div>
            </div>

            <div class="text-center">
              <h6 class="mb-0 f-16">Vehicles Gallery</h6>
            </div>
          </div>
        </div>

        <div
          class="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-12 box-flex"
          v-if="hasUserAccess(userRole, ROLE_PERMISSIONS.DOCUMENTATION)"
        >
          <div
            class="card card-shadow p-3 card-folder"
            @click="$router.push('/tools/documentation')"
          >
            <div class="box-folder">
              <div class="icon-folder primary">
                <i class="flaticon-tools-application"></i>
              </div>
            </div>

            <div class="text-center">
              <h6 class="mb-0 f-16">Documentation</h6>
            </div>
          </div>
        </div>

        <div
          class="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-12 box-flex"
          v-if="hasUserAccess(userRole, ROLE_PERMISSIONS.DESTINATIONS)"
        >
          <div
            class="card card-shadow p-3 card-folder"
            @click="$router.push('/tools/destinations')"
          >
            <div class="box-folder">
              <div class="icon-folder primary">
                <i class="flaticon-tools-destination"></i>
              </div>
            </div>

            <div class="text-center">
              <h6 class="mb-0 f-16">Destinations</h6>
            </div>
          </div>
        </div>

        <div
          class="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-12 box-flex"
          v-if="hasUserAccess(userRole, ROLE_PERMISSIONS.EVENTS)"
        >
          <div
            class="card card-shadow p-3 card-folder"
            @click="$router.push('/tools/events')"
          >
            <div class="box-folder">
              <div class="icon-folder primary">
                <i class="flaticon-tools-event"></i>
              </div>
            </div>

            <div class="text-center">
              <h6 class="mb-0 f-16">Events</h6>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapMutations, mapGetters } from "vuex";
import { goBack, ROLES } from "../../../helpers/commons.helpers";
import { hasUserAccess } from "../../../core/services/security.services";

/**
 * ToolsManager component
 *
 * Renders the tools dashboard and controls access to individual tool cards
 * based on the current user's role. Permissions for each tool are defined in
 * the `ROLE_PERMISSIONS` data object.
 *
 * @module ToolsManager
 */
export default {
  /**
   * Component name used by Vue devtools and route definitions.
   * @type {string}
   */
  name: "Tools",

  /**
   * Reactive component state.
   *
   * Contains a map of tool identifiers to arrays of allowed role identifiers.
   * Use these lists together with `hasUserAccess` to show/hide tool cards.
   *
   * @returns {{ROLE_PERMISSIONS: Object<string, Array<string>>}}
   */
  data() {
    return {
      /**
       * Permission mapping for tools.
       * @type {{VEHICLE_GALLERY: string[], DOCUMENTATION: string[], DESTINATIONS: string[], EVENTS: string[]}}
       */
      ROLE_PERMISSIONS: {
        VEHICLE_GALLERY: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
        DOCUMENTATION: [ROLES.SUPER_ADMIN],
        DESTINATIONS: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
        EVENTS: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER]
      }
    };
  },

  /**
   * Mounted lifecycle hook. Initializes breadcrumb items for the view.
   *
   * @async
   * @returns {Promise<void>} Resolves when breadcrumb mutation has been applied.
   */
  async mounted() {
    this.setItemsBread([
      {
        text: "Dashboard",
        to: "/dashboard"
      },

      {
        text: "Tools",
        active: true
      }
    ]);
  },

  methods: {
    /**
     * Proxy to the imported `hasUserAccess` function.
     *
     * Checks whether the provided `userRole` is present in `allowedRoles`.
     * This method is intended to be used from templates, e.g.:
     * v-if="hasUserAccess(userRole, ROLE_PERMISSIONS.DOCUMENTATION)".
     *
     * @param {string} userRole - The user's role identifier.
     * @param {Array<string>} allowedRoles - Array of allowed role identifiers.
     * @returns {boolean} True if the userRole is allowed, otherwise false.
     */
    hasUserAccess,

    /**
     * Navigate to the previous page using the shared helper `goBack`.
     *
     * @returns {void}
     */
    goToPreviousPage() {
      goBack(this);
    },

    ...mapMutations(["setLoad", "setItemsBread"])
  },

  /**
   * Computed properties mapped from Vuex getters.
   *
   * @property {Object|null} sesion - Session object from the Vuex store (may be null).
   * @property {string|null} userRole - Current user's role identifier.
   */
  computed: {
    ...mapGetters(["sesion", "userRole"])
  }
};
</script>
